
import { useParams } from 'react-router-dom';
import { usePet } from '../hooks/usePet';
import { usePetPosts } from '../hooks/usePosts';
import { Skeleton } from '@/components/ui/skeleton';
import PostCard from '@/components/PostCard';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, MapPin, Heart, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { parseLocalDate } from '@/lib/dateUtils';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import PetProfileActions from '@/components/PetProfileActions';
import { MatchButton } from '@/components/MatchButton';
import { useTranslation } from 'react-i18next';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

const PetProfile = () => {
  const { petId } = useParams<{ petId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const { pet, isLoading: petLoading, error: petError } = usePet(petId);
  const { data: posts, isLoading: postsLoading, error: postsError } = usePetPosts(petId);

  // Check if current user owns this pet
  const isOwner = user?.id === pet?.user_id;

  // Smart back navigation - go to My Pets if owner, otherwise use browser back
  const handleBackNavigation = () => {
    if (isOwner) {
      navigate('/my-pets');
    } else {
      navigate(-1);
    }
  };

  if (petLoading) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-8 w-32" />
          </div>
          <div className="flex flex-col md:flex-row gap-6">
            <Skeleton className="w-full md:w-80 h-80 rounded-lg" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (petError || !pet) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">Error loading pet profile</p>
          <p className="text-sm text-gray-600 mb-4">{petError?.message || 'Pet not found'}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`container mx-auto max-w-4xl p-4 ${pet.is_memorial ? 'memorial-sky-background min-h-screen' : ''}`}>
      {/* Header with back button */}
      <div className="flex items-center justify-between min-h-14 mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackNavigation}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {isOwner ? 'My Pets' : 'Back'}
          </Button>
          <h1 className="text-lg font-semibold">{pet.name}'s Profile</h1>
        </div>
      </div>

      {/* Pet Profile Header */}
      <div className="bg-card rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Pet Photo */}
          <div className="w-full md:w-80 h-80 relative">
            {pet.photo_url ? (
              <img
                src={pet.photo_url}
                alt={pet.name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                <Camera className="h-16 w-16 text-gray-400" />
              </div>
            )}
            {pet.is_memorial && (
              <div className="absolute top-4 right-4">
                <Badge variant="secondary" className="bg-gray-800 text-white">
                  <Heart className="h-3 w-3 mr-1" />
                  In Memory
                </Badge>
              </div>
            )}
          </div>

          {/* Pet Info */}
          <div className="flex-1">
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h2 className="text-3xl font-bold">{pet.name}</h2>
                  {!isOwner && (
                    <MatchButton
                      petId={pet.id}
                      petName={pet.name}
                      petSpecies={pet.species}
                      ownerId={pet.user_id}
                    />
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {pet.species && (
                    <Badge variant="outline">{translateSpeciesSingular(pet.species, t)}</Badge>
                  )}
                  {pet.breed && (
                    <Badge variant="outline">{pet.breed}</Badge>
                  )}
                  {pet.sex && pet.sex !== 'not_specified' && (
                    <Badge variant="outline">
                      {pet.sex === 'male' ? 'Male' : 'Female'}
                    </Badge>
                  )}
                </div>
              </div>

              {pet.birth_date && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>Born {format(parseLocalDate(pet.birth_date), 'MMMM d, yyyy')}</span>
                </div>
              )}

              {/* Pet location display */}
              {pet.location && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>{pet.location}</span>
                </div>
              )}

              {pet.personality_traits && pet.personality_traits.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Personality</h3>
                  <div className="flex flex-wrap gap-2">
                    {pet.personality_traits.map((trait, index) => (
                      <Badge key={index} variant="secondary">
                        {trait}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>Member since {format(new Date(pet.created_at), 'MMMM yyyy')}</span>
              </div>

              {/* Pet Actions */}
              {isOwner && (
                <div className="pt-4">
                  <PetProfileActions pet={pet} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">Memories</h3>
          <span className="text-sm text-gray-500">
            {posts?.length || 0} memories
          </span>
        </div>

        {postsLoading ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden border bg-card text-card-foreground shadow-sm">
                <div className="p-4 flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="h-72 w-full" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : postsError ? (
          <div className="text-center py-8 text-red-500">
            Error loading memories: {postsError.message}
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="space-y-6">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium mb-2">No memories yet</h4>
            <p className="text-gray-500">
              {pet.name} hasn't shared any memories yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PetProfile;
